import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { B } from '../constants';
import { reviews as reviewsAPI } from '../api';
import { useAuth } from '../AuthContext';

function Stars({ value = 0, size = 16, onSelect }) {
  const stars = [1, 2, 3, 4, 5];
  return (
    <span style={{ display: 'inline-flex', gap: 2 }}>
      {stars.map((s) => (
        <span
          key={s}
          role={onSelect ? 'button' : undefined}
          tabIndex={onSelect ? 0 : undefined}
          onClick={onSelect ? () => onSelect(s) : undefined}
          onKeyDown={onSelect ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(s); } } : undefined}
          style={{
            color: s <= Math.round(value) ? B.amber : '#d6d0c4',
            fontSize: size,
            cursor: onSelect ? 'pointer' : 'default',
            lineHeight: 1,
          }}
          aria-label={onSelect ? `${s} star${s > 1 ? 's' : ''}` : undefined}
        >
          ★
        </span>
      ))}
    </span>
  );
}

// Reviews + ratings for a destination. Reads/writes via the /api/reviews
// endpoints, shows the aggregate average, lists reviews, and lets a signed-in
// user create/update/delete their own review.
export default function ReviewsSection({ destinationId }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [state, setState] = useState({ reviews: [], average: 0, count: 0, myReview: null });
  const [loading, setLoading] = useState(true);
  const [rating, setRating] = useState(5);
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);

  const numericId = Number(destinationId);
  const enabled = Number.isInteger(numericId) && numericId > 0;

  const load = useCallback(() => {
    if (!enabled) { setLoading(false); return; }
    setLoading(true);
    reviewsAPI.listForDestination(numericId)
      .then((data) => setState(data))
      .catch(() => setError('Failed to load reviews'))
      .finally(() => setLoading(false));
  }, [numericId, enabled]);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => {
    setBody('');
    setRating(5);
    setEditing(false);
    setError('');
  };

  const startEdit = () => {
    setRating(state.myReview.rating);
    setBody(state.myReview.body || '');
    setEditing(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (state.myReview) {
        await reviewsAPI.update(state.myReview.id, { rating, body });
      } else {
        await reviewsAPI.create({ cityId: numericId, rating, body });
      }
      resetForm();
      load();
    } catch (err) {
      setError(err.message || 'Failed to save review');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!state.myReview) return;
    setSaving(true);
    try {
      await reviewsAPI.remove(state.myReview.id);
      resetForm();
      load();
    } catch (err) {
      setError(err.message || 'Failed to delete review');
    } finally {
      setSaving(false);
    }
  };

  if (!enabled) return null;

  return (
    <div style={{ padding: '8px clamp(16px, 4vw, 40px) 40px' }}>
      <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 28, fontWeight: 700, margin: 0 }}>
          {t('reviews.title')}
        </h2>
        {state.count > 0 && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: B.muted, fontFamily: "'Montserrat',sans-serif", fontSize: 14 }}>
            <Stars value={state.average} />
            <b style={{ color: B.night }}>{state.average.toFixed(1)}</b>
            {t('reviews.basedOn', { count: state.count })}
          </span>
        )}
      </div>

      {loading ? (
        <p style={{ color: B.muted, fontFamily: "'Montserrat',sans-serif" }}>{t('common.loading')}</p>
      ) : (
        <>
          {user && state.myReview && !editing ? (
            <div style={{ background: 'white', border: '1px solid #e7e2d9', borderRadius: 16, padding: '14px 20px', marginBottom: 24, maxWidth: 640, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: "'Montserrat',sans-serif", fontSize: 14, color: B.night, flex: '1 1 200px' }}>
                <Stars value={state.myReview.rating} size={15} />
                {t('reviews.youReviewed')}
              </span>
              <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
                <button type="button" onClick={startEdit} disabled={saving}
                  style={{ background: 'white', color: B.forest, border: `1.5px solid ${B.forest}`, padding: '10px 22px', borderRadius: 999, fontWeight: 700, cursor: 'pointer', fontFamily: "'Montserrat',sans-serif" }}>
                  {t('reviews.edit')}
                </button>
                <button type="button" onClick={handleDelete} disabled={saving}
                  style={{ background: 'white', color: '#dc2626', border: '1.5px solid #dc2626', padding: '10px 22px', borderRadius: 999, fontWeight: 700, cursor: 'pointer', fontFamily: "'Montserrat',sans-serif" }}>
                  {t('reviews.delete')}
                </button>
              </div>
            </div>
          ) : user ? (
            <form onSubmit={handleSubmit} style={{ background: 'white', border: '1px solid #e7e2d9', borderRadius: 16, padding: 20, marginBottom: 24, maxWidth: 640 }}>
              <p style={{ fontWeight: 700, fontFamily: "'Montserrat',sans-serif", marginBottom: 10 }}>
                {state.myReview ? t('reviews.editReview') : t('reviews.writeReview')}
              </p>
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 13, color: B.muted, marginBottom: 6, fontFamily: "'Montserrat',sans-serif" }}>{t('reviews.yourRating')}</label>
                <Stars value={rating} size={26} onSelect={setRating} />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 13, color: B.muted, marginBottom: 6, fontFamily: "'Montserrat',sans-serif" }}>{t('reviews.yourReview')}</label>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder={t('reviews.reviewPlaceholder')}
                  rows={3}
                  maxLength={2000}
                  style={{ width: '100%', borderRadius: 10, border: '1px solid #e7e2d9', padding: 12, fontFamily: "'Montserrat',sans-serif", fontSize: 14, resize: 'vertical', boxSizing: 'border-box' }}
                />
              </div>
              {error && <p style={{ color: '#dc2626', fontSize: 13, marginBottom: 10 }}>{error}</p>}
              <div style={{ display: 'flex', gap: 10 }}>
                <button type="submit" disabled={saving}
                  style={{ background: B.forest, color: 'white', border: 'none', padding: '10px 22px', borderRadius: 999, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1, fontFamily: "'Montserrat',sans-serif" }}>
                  {state.myReview ? t('reviews.update') : t('reviews.submit')}
                </button>
                {editing && (
                  <button type="button" onClick={resetForm} disabled={saving}
                    style={{ background: 'white', color: B.muted, border: `1.5px solid #d6d0c4`, padding: '10px 22px', borderRadius: 999, fontWeight: 700, cursor: 'pointer', fontFamily: "'Montserrat',sans-serif" }}>
                    {t('common.cancel')}
                  </button>
                )}
              </div>
            </form>
          ) : (
            <p style={{ color: B.muted, fontFamily: "'Montserrat',sans-serif", marginBottom: 20 }}>{t('reviews.signInToReview')}</p>
          )}

          {state.reviews.length === 0 ? (
            <p style={{ color: B.muted, fontFamily: "'Montserrat',sans-serif" }}>{t('reviews.noReviews')}</p>
          ) : (
            <div style={{ display: 'grid', gap: 14, maxWidth: 720 }}>
              {state.reviews.map((r) => (
                <div key={r.id} style={{ background: 'white', border: '1px solid #e7e2d9', borderRadius: 14, padding: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                    <div style={{ width: 34, height: 34, borderRadius: '50%', background: `linear-gradient(135deg, ${B.forest}, ${B.mint})`, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 13 }}>
                      {(r.user?.name || 'GB').slice(0, 2).toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, fontFamily: "'Montserrat',sans-serif", fontSize: 14 }}>{r.user?.name || 'Traveler'}</div>
                      <Stars value={r.rating} size={13} />
                    </div>
                    <span style={{ marginLeft: 'auto', fontSize: 12, color: B.muted, fontFamily: "'Montserrat',sans-serif" }}>
                      {new Date(r.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  {r.body && <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 14, color: '#444', lineHeight: 1.6, margin: 0 }}>{r.body}</p>}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
